import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import {
  MagneticButton,
  TiltCard,
  SpotlightCard,
  NumberTicker,
  FlipCard,
} from "../components/showcase/InteractiveBits";
import { CommandPalette, Dock } from "../components/showcase/NavBits";
import { MultiStepForm, FloatingInput } from "../components/showcase/FormBits";

const SECTIONS = [
  { k: "about", l: "About sections" },
  { k: "image", l: "Image styles" },
  { k: "cards", l: "Cards" },
  { k: "forms", l: "Forms" },
  { k: "nav", l: "Navigation" },
  { k: "buttons", l: "Buttons" },
  { k: "data", l: "Data display" },
];

function Section({ id, label, title, children }) {
  return (
    <section id={id} data-testid={`section-${id}`} className="py-24 md:py-32 border-t border-hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16 flex items-end justify-between gap-6">
          <div>
            <span className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
              ◇ {label}
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
              {title}
            </h2>
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

function Variant({ label, children, testId }) {
  return (
    <div data-testid={testId} className="space-y-3">
      <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
        {label}
      </div>
      <div className="rounded-2xl border border-hairline bg-surface p-6 md:p-8">
        {children}
      </div>
    </div>
  );
}

/** Image style A — Lens hover: clip-path circle follows cursor. */
function LensImage({ src }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50, on: false });
  return (
    <div
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        setPos({
          x: ((e.clientX - r.left) / r.width) * 100,
          y: ((e.clientY - r.top) / r.height) * 100,
          on: true,
        });
      }}
      onMouseLeave={() => setPos((p) => ({ ...p, on: false }))}
      className="relative aspect-[4/3] overflow-hidden rounded-xl bg-surface-2"
    >
      <img
        src={src}
        alt=""
        className="absolute inset-0 w-full h-full object-cover grayscale opacity-90"
      />
      <img
        src={src}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        style={{
          clipPath: pos.on
            ? `circle(110px at ${pos.x}% ${pos.y}%)`
            : "circle(0px at 50% 50%)",
        }}
      />
    </div>
  );
}

/** Image style B — Reveal blinds (4 vertical strips parallax in). */
function BlindsImage({ src }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const tDown = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const tUp = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const ys = [tDown, tUp, tDown, tUp];
  return (
    <div ref={ref} className="relative aspect-[4/3] overflow-hidden rounded-xl bg-surface-2 grid grid-cols-4 gap-1">
      {ys.map((y, i) => (
        <motion.div key={i} style={{ y }} className="relative overflow-hidden">
          <img
            src={src}
            alt=""
            className="absolute h-full object-cover"
            style={{ left: `${-i * 100}%`, width: "400%" }}
          />
        </motion.div>
      ))}
    </div>
  );
}

/** Image style C — Ken Burns slow zoom on hover. */
function KenBurns({ src }) {
  return (
    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-surface-2">
      <img
        src={src}
        alt=""
        className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-125 transition-transform duration-[4000ms] ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute bottom-4 left-4 font-display text-xl text-white">Ken Burns</div>
    </div>
  );
}

/** Image style D — Duotone overlay. */
function Duotone({ src }) {
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-surface-2">
      <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div
        className="absolute inset-0 mix-blend-color"
        style={{ background: "linear-gradient(135deg, #6366f1, #ec4899)" }}
      />
      <div className="absolute inset-0 mix-blend-screen bg-black/30" />
    </div>
  );
}

/** Marquee of items. */
function Marquee({ items, reverse = false }) {
  const list = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-hairline py-6 bg-surface">
      <div className={`flex whitespace-nowrap ${reverse ? "marquee-reverse" : "marquee"}`}>
        {list.map((i, idx) => (
          <span key={idx} className="px-10 font-display text-3xl md:text-4xl text-secondary-x">
            {i}
          </span>
        ))}
      </div>
    </div>
  );
}

const SAMPLE_IMG =
  "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85";

export default function ComponentsPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Components"
        title="Pieces, on display."
        accent="display."
        kicker="An evolving library of immersive, interactive components we use to build Ulties products. Hover, scroll, click — everything reacts."
      />

      {/* Sticky in-page nav */}
      <div className="sticky top-16 md:top-20 z-30 bg-canvas/80 backdrop-blur-2xl border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-2 overflow-x-auto">
          <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mr-3 shrink-0">
            Jump to
          </div>
          {SECTIONS.map((s) => (
            <a
              key={s.k}
              href={`#${s.k}`}
              data-testid={`jumpto-${s.k}`}
              className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-hairline-strong text-secondary-x hover:text-primary-x hover:bg-glass-hover transition-colors"
            >
              {s.l}
            </a>
          ))}
        </div>
      </div>

      {/* ABOUT SECTIONS */}
      <Section id="about" label="About sections" title="Tell your story, differently.">
        <div className="grid lg:grid-cols-2 gap-8">
          <Variant label="A · Editorial split" testId="about-editorial">
            <div className="grid md:grid-cols-2 gap-8 items-end">
              <div>
                <div className="font-display text-7xl md:text-8xl font-bold tracking-[-0.04em] leading-none text-primary-x">
                  Est.<br />2019
                </div>
                <p className="mt-6 text-secondary-x leading-relaxed">
                  A boutique studio of senior engineers building software that has to work — from realtime trading floors to clinical environments.
                </p>
              </div>
              <ul className="space-y-3 border-l border-hairline-strong pl-6">
                {["Senior-only bench", "Weekly shipping cadence", "Type-safe by default", "Audit-ready security"].map((t) => (
                  <li key={t} className="text-primary-x text-sm flex items-center gap-3">
                    <span className="h-1 w-3 bg-primary-x rounded-full" style={{ background: "var(--text-primary)" }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Variant>

          <Variant label="B · Scrubbed timeline" testId="about-timeline">
            <ol className="space-y-6">
              {[
                ["2019", "Founded in Lisbon. Two engineers, one rented office."],
                ["2021", "First AI agent in production — Atlas Capital."],
                ["2023", "Opened Bengaluru studio. Team of 11."],
                ["2025", "47 products shipped. Still senior-only."],
              ].map(([y, t]) => (
                <li key={y} className="grid grid-cols-[80px_1fr] gap-6 items-baseline">
                  <span className="font-mono-x text-secondary-x text-sm">{y}</span>
                  <span className="text-primary-x">{t}</span>
                </li>
              ))}
            </ol>
          </Variant>

          <Variant label="C · Big-quote" testId="about-quote">
            <SpotlightCard className="rounded-xl border border-hairline-strong p-8">
              <div className="font-display text-3xl md:text-4xl leading-snug text-primary-x text-balance">
                &ldquo;We optimise for the work, not the headcount. Every engagement is led by someone who&apos;s built the thing being hired to build.&rdquo;
              </div>
              <div className="mt-8 flex items-center gap-3 text-secondary-x text-sm">
                <span className="h-8 w-8 rounded-full bg-primary-x" style={{ background: "var(--text-primary)" }} />
                <span>Mira Kahn — Principal Engineer</span>
              </div>
            </SpotlightCard>
          </Variant>

          <Variant label="D · Manifesto numbers" testId="about-manifesto">
            <div className="grid grid-cols-3 gap-6 text-center">
              {[
                ["47", "Products"],
                ["9", "Countries"],
                ["0", "Audit findings"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="font-display text-5xl md:text-6xl font-bold tracking-[-0.03em] text-primary-x">
                    <NumberTicker value={parseInt(v)} />
                  </div>
                  <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </Variant>
        </div>
      </Section>

      {/* IMAGE STYLES */}
      <Section id="image" label="Image styles" title="Photography, with intent.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Variant label="A · Lens reveal" testId="img-lens">
            <LensImage src={SAMPLE_IMG} />
          </Variant>
          <Variant label="B · Blinds parallax" testId="img-blinds">
            <BlindsImage src={SAMPLE_IMG} />
          </Variant>
          <Variant label="C · Ken Burns" testId="img-kenburns">
            <KenBurns src={SAMPLE_IMG} />
          </Variant>
          <Variant label="D · Duotone" testId="img-duotone">
            <Duotone src={SAMPLE_IMG} />
          </Variant>
        </div>

        <div className="mt-10">
          <Marquee
            items={[
              "Helios",
              "Sentinel",
              "Atlas",
              "Lumen",
              "Northwind",
              "Quanta",
              "Aperture",
            ]}
          />
        </div>
      </Section>

      {/* CARDS */}
      <Section id="cards" label="Cards" title="Cards that respond.">
        <div className="grid md:grid-cols-3 gap-6">
          <Variant label="A · Tilt card" testId="card-tilt">
            <TiltCard className="rounded-xl border border-hairline-strong bg-surface-2 p-8 aspect-square flex flex-col justify-between">
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                Hover me
              </div>
              <div>
                <div className="font-display text-3xl text-primary-x">Helios</div>
                <div className="text-secondary-x text-sm mt-2">AI · Web · 2025</div>
              </div>
            </TiltCard>
          </Variant>

          <Variant label="B · Spotlight card" testId="card-spotlight">
            <SpotlightCard className="rounded-xl border border-hairline-strong p-8 aspect-square flex flex-col justify-between bg-surface-2">
              <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                Move cursor
              </div>
              <div>
                <div className="font-display text-3xl text-primary-x">Sentinel</div>
                <div className="text-secondary-x text-sm mt-2">Cybersec · 2024</div>
              </div>
            </SpotlightCard>
          </Variant>

          <Variant label="C · Flip card" testId="card-flip">
            <div className="aspect-square">
              <FlipCard
                testId="flip-1"
                front={
                  <>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                      Click to flip
                    </div>
                    <div>
                      <div className="font-display text-3xl text-primary-x">Atlas</div>
                      <div className="text-secondary-x text-sm mt-2">AI Agents</div>
                    </div>
                  </>
                }
                back={
                  <>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">
                      Outcome
                    </div>
                    <div>
                      <div className="font-display text-3xl text-primary-x">3.2× memos</div>
                      <div className="text-secondary-x text-sm mt-2">−68% bookkeeping time</div>
                    </div>
                  </>
                }
              />
            </div>
          </Variant>
        </div>
      </Section>

      {/* FORMS */}
      <Section id="forms" label="Forms" title="Forms people finish.">
        <div className="grid lg:grid-cols-2 gap-6">
          <Variant label="A · Multi-step brief" testId="form-multistep">
            <MultiStepForm testId="form-multistep" />
          </Variant>
          <Variant label="B · Floating-label inline" testId="form-inline">
            <FloatingInlineForm />
          </Variant>
        </div>
      </Section>

      {/* NAVIGATION */}
      <Section id="nav" label="Navigation" title="Ways around the studio.">
        <div className="grid lg:grid-cols-2 gap-6">
          <Variant label="A · Command palette (⌘K)" testId="nav-cmdk">
            <div className="flex flex-col items-start gap-6">
              <p className="text-secondary-x">Press <kbd className="rounded border border-hairline-strong bg-canvas px-1.5 py-0.5 text-xs font-mono-x text-primary-x">⌘K</kbd> anywhere on this page, or click below.</p>
              <CommandPalette />
            </div>
          </Variant>

          <Variant label="B · Magnified dock" testId="nav-dock">
            <div className="flex justify-center">
              <Dock
                items={[
                  { k: "home", l: "Home", icon: "✦" },
                  { k: "services", l: "Services", icon: "◐" },
                  { k: "work", l: "Work", icon: "◇" },
                  { k: "process", l: "Process", icon: "◈" },
                  { k: "about", l: "About", icon: "◉" },
                  { k: "contact", l: "Contact", icon: "✉" },
                ]}
              />
            </div>
          </Variant>
        </div>
      </Section>

      {/* BUTTONS */}
      <Section id="buttons" label="Buttons" title="Pressable. Magnetic.">
        <div className="grid md:grid-cols-3 gap-6">
          <Variant label="A · Magnetic" testId="btn-magnetic">
            <div className="h-32 flex items-center justify-center">
              <MagneticButton testId="magnetic-btn">
                Pull me →
              </MagneticButton>
            </div>
          </Variant>
          <Variant label="B · Shimmer outline" testId="btn-shimmer">
            <div className="h-32 flex items-center justify-center">
              <ShimmerButton />
            </div>
          </Variant>
          <Variant label="C · Long-press confirm" testId="btn-longpress">
            <div className="h-32 flex items-center justify-center">
              <LongPressButton />
            </div>
          </Variant>
        </div>
      </Section>

      {/* DATA */}
      <Section id="data" label="Data display" title="Numbers, animated.">
        <div className="grid md:grid-cols-3 gap-6">
          <Variant label="A · Counter" testId="data-counter">
            <div className="text-center">
              <div className="font-display text-7xl md:text-8xl font-bold tracking-[-0.04em] text-primary-x">
                <NumberTicker value={2847} suffix="" />
              </div>
              <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
                deploys this quarter
              </div>
            </div>
          </Variant>
          <Variant label="B · Sparkline-ish" testId="data-spark">
            <Spark />
          </Variant>
          <Variant label="C · Progress ring" testId="data-ring">
            <ProgressRing value={87} />
          </Variant>
        </div>
      </Section>

      <section className="border-t border-hairline py-24 md:py-32 text-center bg-surface">
        <div className="max-w-3xl mx-auto px-6">
          <h3 className="font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
            Want one of these in your product?
          </h3>
          <Link
            to="/contact"
            data-testid="components-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
          >
            Start a project →
          </Link>
        </div>
      </section>

      {/* extra marquee styles */}
      <style>{`
        @keyframes marquee-rev { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        .marquee-reverse { animation: marquee-rev 40s linear infinite; }
      `}</style>
    </PageShell>
  );
}

/* ---------- Local sub-components used only by this page ---------- */

function FloatingInlineForm() {
  const [v, setV] = useState({ name: "", email: "" });
  return (
    <div className="space-y-8">
      <FloatingInput
        label="Full name"
        value={v.name}
        onChange={(e) => setV({ ...v, name: e.target.value })}
        testId="floating-name"
      />
      <FloatingInput
        label="Work email"
        value={v.email}
        onChange={(e) => setV({ ...v, email: e.target.value })}
        testId="floating-email"
        type="email"
      />
      <div className="text-xs text-muted-x">
        Showcase only — labels animate when focused or filled.
      </div>
    </div>
  );
}

function ShimmerButton() {
  return (
    <button
      type="button"
      data-testid="shimmer-btn"
      className="relative inline-flex items-center gap-2 rounded-full border border-hairline-strong px-6 py-3 text-sm text-primary-x overflow-hidden"
    >
      <span className="relative z-10">Subscribe</span>
      <span
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 animate-[shimmer_2s_linear_infinite]"
        style={{
          background:
            "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)",
        }}
      />
      <style>{`@keyframes shimmer { 0% { transform: translateX(-50%); } 100% { transform: translateX(450%); } }`}</style>
    </button>
  );
}

function LongPressButton() {
  const [p, setP] = useState(0);
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const start = () => {
    if (done) return;
    const t0 = performance.now();
    const tick = (t) => {
      const next = Math.min(100, ((t - t0) / 1200) * 100);
      setP(next);
      if (next < 100) ref.current = requestAnimationFrame(tick);
      else {
        setDone(true);
      }
    };
    ref.current = requestAnimationFrame(tick);
  };
  const stop = () => {
    cancelAnimationFrame(ref.current);
    if (!done) setP(0);
  };
  return (
    <button
      type="button"
      data-testid="longpress-btn"
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchEnd={stop}
      className="relative inline-flex items-center gap-2 rounded-full border border-hairline-strong px-6 py-3 text-sm text-primary-x overflow-hidden"
      style={{ minWidth: 200 }}
    >
      <span
        className="absolute inset-y-0 left-0"
        style={{
          width: `${p}%`,
          background: "var(--text-primary)",
          opacity: 0.18,
          transition: done ? "none" : undefined,
        }}
      />
      <span className="relative">{done ? "Confirmed ✓" : "Hold to confirm"}</span>
    </button>
  );
}

function Spark() {
  const pts = [4, 7, 5, 9, 12, 10, 14, 13, 18, 22, 19, 26];
  const max = Math.max(...pts);
  const w = 220;
  const h = 80;
  const d = pts
    .map((p, i) => `${(i / (pts.length - 1)) * w},${h - (p / max) * h}`)
    .join(" ");
  return (
    <div className="flex flex-col items-center">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-primary-x">
        <polyline points={d} fill="none" stroke="currentColor" strokeWidth="2" />
        <polyline
          points={`0,${h} ${d} ${w},${h}`}
          fill="currentColor"
          opacity="0.08"
        />
      </svg>
      <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
        deploy velocity · 12 wk
      </div>
    </div>
  );
}

function ProgressRing({ value = 75 }) {
  const c = 2 * Math.PI * 36;
  const off = useMotionValue(c);
  const dash = useSpring(off, { stiffness: 100, damping: 20 });
  React.useEffect(() => {
    off.set(c - (value / 100) * c);
  }, [value, off, c]);
  return (
    <div className="flex flex-col items-center">
      <div className="relative h-24 w-24">
        <svg viewBox="0 0 80 80" className="rotate-[-90deg] absolute inset-0">
          <circle cx="40" cy="40" r="36" fill="none" stroke="var(--hairline-strong)" strokeWidth="6" />
          <motion.circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="var(--text-primary)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={c}
            style={{ strokeDashoffset: dash }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-display text-2xl text-primary-x">
          {value}%
        </div>
      </div>
      <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
        eval coverage
      </div>
    </div>
  );
}
